import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { v4 as uuid } from 'uuid'
import { StoredIndividualization, Template } from '../types/Individualization'
import { Article } from '../types/Article'
import { fabric } from 'fabric'
import { rescaleFabricObject } from '../helpers/resizeEverything'
import { useFetchData } from '../hooks/useFetchData'
import { Font } from '../types/Font'
import { MediaCategory } from '../types/Media'
import { useFetchSingle } from '../hooks/useFetchSingle'
import { api, ApiRoutes } from '../services/ApiService'
import { useLocation, useNavigate } from 'react-router-dom'
import { showToast } from '../services/ToastService'

type IndividualizationContextType = {
    individualizationId: string | number
    steps: string[]
    completedSteps: number[]
    errors: string[]
    activeStep: number
    visitedSteps: number[]
    changeActiveStep: (stepNumber: number) => void
    article: Article | null
    setArticle: React.Dispatch<React.SetStateAction<Article | null>>
    fetchArticle: (kArtikel?: string | number) => void
    templates: Template[]
    uploadTemplate: (src: string, id?: string | number | null) => void
    removeTemplate: (id: string | number) => void
    changeTemplateName: (
        id: string | number,
        name: string,
        name_multi?: string
    ) => void
    changeTemplateSurcharge: (id: string | number, kArtikel: string) => void
    spots: fabric.Object[]
    addSpot: (spot: fabric.Object) => void
    removeSpot: (spot_id: string | number, canvas: fabric.Canvas) => void
    syncSpot: (spot: fabric.Object) => void
    syncHelper: (helper: fabric.Circle) => void
    updateSpot: (
        spot_id: string | number,
        property: string,
        value: string
    ) => void
    updateSpotData: (
        spot_id: string | number,
        property: string,
        value: string | number | boolean | number[]
    ) => void
    addEdgeToPolygon: (
        polygon_id: string | number,
        template_id: string | number,
        canvas: fabric.Canvas
    ) => void
    removeEdgeFromPolygon: (polygon_id: string | number) => void
    initializeHelpers: (polygon: fabric.Polygon) => void
    helpers: Map<string | number, fabric.Circle[]>
    addHelper: (spot: fabric.Circle) => void
    examples: fabric.Object[]
    setExamples: React.Dispatch<React.SetStateAction<fabric.Object[]>>
    measurements: {
        start: fabric.Rect
        end: fabric.Rect
        line: fabric.Rect
    }[]
    setMeasurements: React.Dispatch<
        React.SetStateAction<
            {
                start: fabric.Rect
                end: fabric.Rect
                line: fabric.Rect
            }[]
        >
    >
    globalSurcharge: string | null
    updateGlobalSurcharge: (kArtikel: string | null) => void
    submitIndividualization: (
        method: 'store' | 'update' | 'duplicate'
    ) => Promise<void>
    fonts: Font[]
    categories: MediaCategory[]
    canvas: fabric.Canvas
    activeSpotId: string | number | null
    activeSpot: fabric.Object | undefined
    changeActiveSpot: (spot_id: string | number | null) => void

    activeTemplateId: string | number | null
    setActiveTemplateId: React.Dispatch<
        React.SetStateAction<string | number | null>
    >
    activeTemplate: Template | undefined

    chosenVariations: Record<string, number[]>
    setChosenVariations: React.Dispatch<
        React.SetStateAction<Record<string, number[]>>
    >

    hasMeasurement: boolean
    setHasMeasurement: React.Dispatch<React.SetStateAction<boolean>>
    addMeasurement: (
        template_id: number | string,
        id?: string | number,
        color?: string,
        center?: fabric.Point,
        orientation?: 'horizontal' | 'vertical',
        length?: number,
        real_length?: number | string | undefined
    ) => {
        line: fabric.Rect
        start: fabric.Rect
        end: fabric.Rect
    }
}

const IndividualizationContext = createContext<
    IndividualizationContextType | undefined
>(undefined)

export const IndividualizationContextProvider = ({
    children,
    individualization,
    step,
}: {
    children: React.ReactNode
    individualization?: StoredIndividualization
    step?: string
}): JSX.Element => {
    const location = useLocation()
    const navigate = useNavigate()

    const pathIndex = useMemo(
        () => ['articles', 'templates', 'dimensions', 'areas', 'surcharges'],
        []
    )

    const findIndex = (path: string) => {
        const index = pathIndex.findIndex((p) => {
            return path.includes(p)
        })

        return index !== -1 ? index : 0
    }

    const previousPath = useRef<string>('')
    const changeSubPath = useCallback(
        (index: number) => {
            const subPath =
                index < pathIndex.length ? pathIndex[index] : pathIndex[0]

            if (previousPath.current === subPath) return
            previousPath.current = subPath

            const containsSubPath = pathIndex.some((p) =>
                location.pathname.includes('/' + p)
            )

            const pathParts = location.pathname.split('/')
            const path = (containsSubPath ? pathParts.slice(0, -1) : pathParts)
                .concat(subPath)
                .join('/')

            navigate(path)
        },
        [location.pathname, navigate, pathIndex]
    )

    const { t } = useTranslation(['individualization'])
    const steps = useMemo(() => {
        return [
            t('progress.select_article'),
            t('progress.upload_templates'),
            t('progress.set_dimensions'),
            t('progress.define_areas'),
            t('progress.determine_surcharges'),
        ]
    }, [t])

    const {
        data: article,
        fetch: fetchArticle,
        setData: setArticle,
    } = useFetchSingle<Article>(ApiRoutes.ARTICLES)

    const [activeStep, setActiveStep] = useState<number>(
        step ? findIndex(step) : 0
    )
    const [visitedSteps, setVisitedSteps] = useState<number[]>([])
    const [completedSteps, setCompletedSteps] = useState<number[]>([])
    const [individualizationId, setIndividualizationId] = useState<
        number | string
    >(uuid())
    const [templates, setTemplates] = useState<Template[]>([])
    const [globalSurcharge, setGlobalSurcharge] = useState<string | null>(null)
    const [chosenVariations, setChosenVariations] = useState<
        Record<string, number[]>
    >({})
    const [hasMeasurement, setHasMeasurement] = useState<boolean>(false)

    useEffect(() => {
        changeSubPath(activeStep)
    }, [activeStep, changeSubPath])

    const filteredVariations = useMemo(() => {
        const filtered: Record<string, number[]> = {}

        Object.keys(chosenVariations).forEach((key) => {
            if (chosenVariations[key].length > 0) {
                filtered[key] = chosenVariations[key]
            }
        })

        return Object.keys(filtered).length > 0 ? filtered : null
    }, [chosenVariations])

    //DefineAreas
    const [spots, setSpots] = useState<fabric.Object[]>([])
    const [helpers, setHelpers] = useState<
        Map<string | number, fabric.Circle[]>
    >(new Map())
    const [examples, setExamples] = useState<fabric.Object[]>([])
    const [activeTemplateId, setActiveTemplateId] = useState<
        string | number | null
    >(null)
    const [activeSpotId, setActiveSpotId] = useState<string | number | null>(
        null
    )
    const [measurements, setMeasurements] = useState<
        {
            start: fabric.Rect
            end: fabric.Rect
            line: fabric.Rect
        }[]
    >([])

    const { data: fonts, fetch: fetchFonts } = useFetchData<Font>(
        ApiRoutes.FONTS
    )

    const { data: categories, fetch: fetchCategories } =
        useFetchData<MediaCategory>(ApiRoutes.MEDIA_CATEGORIES)

    const canvas = useMemo(() => {
        return new fabric.Canvas(document.createElement('canvas'), {
            backgroundColor: 'white',
            selection: false,
            renderOnAddRemove: true,
            preserveObjectStacking: true,
        })
    }, [])

    const activeSpot: fabric.Object | undefined = useMemo(() => {
        return spots.find((spot) => {
            return (
                spot.data.id === activeSpotId &&
                spot.data.template_id === activeTemplateId
            )
        })
    }, [spots, activeSpotId, activeTemplateId])

    const activeTemplate: Template | undefined = useMemo(() => {
        return templates.find((template) => template.id === activeTemplateId)
    }, [activeTemplateId, templates])

    const changeActiveSpot = (spot_id: string | number | null) => {
        setActiveSpotId(spot_id)
    }

    const changeActiveStep = (stepNumber: number) => {
        if (stepNumber <= steps.length) {
            setActiveStep((prevStep) => {
                if (!visitedSteps.includes(prevStep)) {
                    setVisitedSteps((prevVisited) => {
                        if (prevVisited.includes(prevStep)) return prevVisited
                        return [...prevVisited, prevStep]
                    })
                }
                return stepNumber
            })
        }
    }

    const uploadTemplate = (src: string, id: string | number | null = null) => {
        if (!id) {
            const temp: Template = {
                id: uuid(),
                name: '',
                src: src,
                surcharge_kArtikel: null,
                individualization_id: individualizationId,
            }
            setTemplates((prevTemplates) => [...prevTemplates, temp])
        } else {
            setTemplates((prevTemplates) => {
                return prevTemplates.map((template) => {
                    if (template.id === id) {
                        template.src = src
                    }
                    return template
                })
            })
        }
    }

    const changeTemplateName = (
        id: string | number,
        name: string,
        name_multi?: string
    ) => {
        setTemplates((prevTemplates) => {
            return prevTemplates.map((template) => {
                if (template.id === id) {
                    template.name = name
                    template.name_multi = name_multi
                }
                return template
            })
        })
    }

    const changeTemplateSurcharge = (id: string | number, kArtikel: string) => {
        setTemplates((prevTemplates) => {
            return prevTemplates.map((template) => {
                if (template.id === id) {
                    template.surcharge_kArtikel = kArtikel
                }
                return template
            })
        })
    }

    const removeTemplate = (id: string | number) => {
        setTemplates((prevTemplates) =>
            prevTemplates.filter((template) => template.id !== id)
        )
        setSpots((prevSpots) =>
            prevSpots.filter((spot) => spot.data.template_id !== id)
        )
        setMeasurements((prevMeasurements) =>
            prevMeasurements.filter(
                (measurement) => measurement.line.data.template_id !== id
            )
        )
    }

    const addSpot = (spot: fabric.Object) => {
        setSpots((prevSpots) => [...prevSpots, spot])
    }

    const removeSpot = (id: string | number, canvas: fabric.Canvas) => {
        const objects = canvas
            .getObjects()
            .filter(
                (obj: fabric.Object) =>
                    obj.data?.id === id || obj.data?.polygon_id === id
            )

        objects.forEach((obj) => {
            canvas.remove(obj)
        })

        setSpots((prevSpots) => prevSpots.filter((spot) => spot.data.id !== id))
        setHelpers((prevHelpers) => {
            const map = new Map(prevHelpers)
            map.delete(id)
            return map
        })
    }

    const syncSpot = useCallback((syncSpot: fabric.Object) => {
        setSpots((prevSpots) => [
            ...prevSpots.filter((spot) => spot.data.id !== syncSpot.data.id),
            syncSpot,
        ])
    }, [])

    const removeExample = useCallback(
        (spot_id: string | number) => {
            const obj = canvas
                .getObjects()
                .find(
                    (o) =>
                        o.data.type === 'Example' && o.data.spot_id === spot_id
                )
            if (obj) canvas.remove(obj)

            setExamples((prevExamples) =>
                prevExamples.filter(
                    (example) => example.data.spot_id !== spot_id
                )
            )
        },
        [canvas]
    )

    const updatePolygon = useCallback(
        (polygon_id: string | number, index: number, point: fabric.Point) => {
            setSpots((prevSpots) => {
                return prevSpots.map((spot) => {
                    if (spot.data.id === polygon_id) {
                        ;((spot as fabric.Polygon).points as fabric.Point[])[
                            index
                        ] = point

                        const { width, height, left, top } = (
                            spot as fabric.Polygon
                        )._calcDimensions()
                        ;(spot as fabric.Polygon)
                            .set({
                                width,
                                height,
                                pathOffset: new fabric.Point(
                                    left + width / 2,
                                    top + height / 2
                                ),
                                dirty: true,
                            })
                            .setCoords()

                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-expect-error
                        ;(spot as fabric.Polygon)._setPositionDimensions({})
                    }
                    return spot
                })
            })
        },
        []
    )

    const syncHelper = useCallback(
        (helper: fabric.Circle) => {
            setHelpers((prevHelpers) => {
                const map = new Map(prevHelpers)
                const current = map.get(helper.data.polygon_id) ?? []
                map.set(helper.data.polygon_id, [
                    ...current.filter(
                        (c) => c.data.index !== helper.data.index
                    ),
                    helper,
                ])
                return map
            })
            updatePolygon(
                helper.data.polygon_id,
                helper.data.index,
                helper.getCenterPoint()
            )
        },
        [updatePolygon]
    )

    const updateMeasurement = (selected: fabric.Rect) => {
        setMeasurements((prevMeasurements) => {
            return [...prevMeasurements].map((measurement) => {
                if (selected.data.id !== measurement.start.data.id) {
                    return measurement
                }

                const orientation: string =
                    measurement.line.data.orientation ?? 'horizontal'
                if (['start', 'end'].includes(selected.data.sub_type)) {
                    selected = rescaleFabricObject(selected) as fabric.Rect

                    if (selected.data.sub_type === 'start') {
                        measurement.start = selected
                    } else if (selected.data.sub_type === 'end') {
                        measurement.end = selected
                    }

                    measurement.line.set({
                        left:
                            ((measurement.start.left as number) +
                                (measurement.end.left as number)) /
                            2,
                        top:
                            ((measurement.start.top as number) +
                                (measurement.end.top as number)) /
                            2,
                        width:
                            orientation === 'horizontal'
                                ? Math.abs(
                                      (measurement.start.left as number) -
                                          (measurement.end.left as number)
                                  )
                                : 5,
                        height:
                            orientation === 'horizontal'
                                ? 5
                                : Math.abs(
                                      (measurement.start.top as number) -
                                          (measurement.end.top as number)
                                  ),
                    })

                    measurement.line.setCoords()
                } else {
                    measurement.start.set({
                        left:
                            orientation === 'horizontal'
                                ? (measurement.line.left as number) -
                                  (measurement.line.width as number) / 2
                                : (measurement.line.left as number),
                        top:
                            orientation === 'horizontal'
                                ? (measurement.line.top as number)
                                : (measurement.line.top as number) -
                                  (measurement.line.height as number) / 2,
                    })

                    measurement.start.setCoords()

                    measurement.end.set({
                        left:
                            orientation === 'horizontal'
                                ? (measurement.line.left as number) +
                                  (measurement.line.width as number) / 2
                                : (measurement.line.left as number),
                        top:
                            orientation === 'horizontal'
                                ? (measurement.line.top as number)
                                : (measurement.line.top as number) +
                                  (measurement.line.height as number) / 2,
                    })

                    measurement.end.setCoords()
                }

                return { ...measurement }
            })
        })
    }

    const updateSpotData = (
        spot_id: string | number,
        property: string,
        value: string | number | boolean | number[]
    ) => {
        setSpots((prevSpots) => {
            return prevSpots.map((spot) => {
                if (spot.data.id === spot_id) {
                    spot.data = { ...spot.data, [property]: value }
                }
                return spot
            })
        })
    }

    const updateSpot = (
        spot_id: string | number,
        property: string,
        value: string
    ) => {
        setSpots((prevSpots) => {
            return prevSpots.map((spot) => {
                if (spot.data.id === spot_id) {
                    spot.set(property as keyof fabric.Object, parseFloat(value))
                }
                return spot
            })
        })
    }

    const addEdgeToPolygon = (
        polygon_id: string | number,
        template_id: string | number,
        canvas: fabric.Canvas
    ) => {
        setSpots((prevSpots) => {
            return prevSpots.map((spot) => {
                if (spot.data.id === polygon_id) {
                    const points = (spot as fabric.Polygon)
                        .points as fabric.Point[]
                    if (points.length > 0) {
                        const next = new fabric.Point(
                            (points[0].x + points[points.length - 1].x) / 2,
                            (points[0].y + points[points.length - 1].y) / 2
                        )

                        ;(spot as fabric.Polygon).points = [
                            ...((spot as fabric.Polygon)
                                .points as fabric.Point[]),
                            next,
                        ]

                        spot.set({ dirty: true })
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-expect-error
                        ;(spot as fabric.Polygon)._setPositionDimensions({})

                        const edge = new fabric.Circle({
                            originX: 'center',
                            originY: 'center',
                            left: next.x,
                            top: next.y,
                            radius: 6,
                            angle: 0,
                            fill: '#FC0',
                            centeredScaling: true,
                            selectable: true,
                            transparentCorners: true,
                            hasBorders: false,
                            hasControls: false,
                            hasRotatingPoint: false,
                            data: {
                                id: uuid(),
                                type: 'Edge',
                                index: points.length,
                                polygon_id: polygon_id,
                                template_id: template_id,
                            },
                            opacity: 1,
                        })

                        addHelper(edge)
                        canvas.add(edge)
                        canvas.requestRenderAll()
                    }
                }
                return spot
            })
        })
    }

    const addMeasurement = useCallback(
        (
            template_id: number | string,
            id: string | number = uuid(),
            color: string = '#FC0',
            center: fabric.Point = new fabric.Point(500, 500),
            orientation: 'horizontal' | 'vertical' = 'horizontal',
            length: number = 800,
            real_length: number | string | undefined = undefined
        ) => {
            const startPoint =
                orientation === 'horizontal'
                    ? new fabric.Point(center.x - length / 2, center.y)
                    : new fabric.Point(center.x, center.y - length / 2)
            const endPoint =
                orientation === 'horizontal'
                    ? new fabric.Point(center.x + length / 2, center.y)
                    : new fabric.Point(center.x, center.y + length / 2)

            const start = new fabric.Rect({
                originX: orientation === 'horizontal' ? 'right' : 'center',
                originY: orientation === 'horizontal' ? 'center' : 'bottom',
                left: startPoint.x,
                top: startPoint.y,
                width: orientation === 'horizontal' ? 5 : 50,
                height: orientation === 'horizontal' ? 50 : 5,
                angle: 0,
                fill: color,
                centeredScaling: true,
                selectable: true,
                transparentCorners: true,
                hasBorders: false,
                hasControls: false,
                hasRotatingPoint: false,
                lockMovementY: orientation === 'horizontal',
                lockMovementX: orientation !== 'horizontal',
                data: {
                    id: id,
                    template_id: template_id,
                    type: 'Measurement',
                    sub_type: 'start',
                },
            })

            const end = new fabric.Rect({
                originX: orientation === 'horizontal' ? 'left' : 'center',
                originY: orientation === 'horizontal' ? 'center' : 'top',
                left: endPoint.x,
                top: endPoint.y,
                width: orientation === 'horizontal' ? 5 : 50,
                height: orientation === 'horizontal' ? 50 : 5,
                angle: 0,
                fill: color,
                centeredScaling: true,
                selectable: true,
                transparentCorners: true,
                hasBorders: true,
                hasControls: false,
                hasRotatingPoint: false,
                lockMovementY: orientation === 'horizontal',
                lockMovementX: orientation !== 'horizontal',
                data: {
                    id: id,
                    template_id: template_id,
                    type: 'Measurement',
                    sub_type: 'end',
                },
            })

            const line = new fabric.Rect({
                originX: 'center',
                originY: 'center',
                centeredScaling: true,
                left: center.x,
                top: center.y,
                width: orientation === 'horizontal' ? length : 5,
                height: orientation === 'horizontal' ? 5 : length,
                angle: 0,
                fill: color,
                selectable: true,
                hasBorders: true,
                hasControls: false,
                data: {
                    id: id,
                    template_id: template_id,
                    type: 'Measurement',
                    sub_type: 'line',
                    orientation: orientation,
                    real_length: real_length,
                },
            })

            return {
                line: line,
                start: start,
                end: end,
            }
        },
        []
    )

    const removeEdgeFromPolygon = (polygon_id: string | number) => {
        setSpots((prevSpots) => {
            return prevSpots.map((spot) => {
                if (spot.data.id === polygon_id) {
                    const points = (spot as fabric.Polygon)
                        .points as fabric.Point[]

                    if (points.length > 3) {
                        points.pop()
                        ;(spot as fabric.Polygon).points = points
                        spot.set({ dirty: true })

                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-expect-error
                        ;(spot as fabric.Polygon)._setPositionDimensions({})

                        resetHelpers(spot as fabric.Polygon)
                        initializeHelpers(spot as fabric.Polygon)
                    }
                }
                return spot
            })
        })
    }

    const updateGlobalSurcharge = (kArtikel: string | null) => {
        if (kArtikel) setGlobalSurcharge(kArtikel)
        else setGlobalSurcharge(null)
    }

    const addHelper = (helper: fabric.Circle) => {
        setHelpers((prevHelpers) => {
            const map = new Map(prevHelpers)
            const current = map.get(helper.data.polygon_id) ?? []
            map.set(helper.data.polygon_id, [
                ...(current as fabric.Circle[]),
                helper,
            ])
            return map
        })
    }

    const resetHelpers = useCallback(
        (polygon: fabric.Polygon) => {
            const polygon_id = polygon.data?.id

            if (!polygon_id) return

            setHelpers((prevHelpers) => {
                const map = new Map(prevHelpers)
                const current = map.get(polygon_id)

                current?.forEach((helper) => {
                    canvas.remove(helper)
                })

                map.delete(polygon_id)

                return map
            })
        },
        [canvas]
    )

    const initializeHelpers = useCallback(
        (polygon: fabric.Polygon) => {
            const edges: fabric.Circle[] = []

            if (!polygon.points) return

            polygon.points.forEach((point, index) => {
                edges.push(
                    new fabric.Circle({
                        originX: 'center',
                        originY: 'center',
                        left: point.x,
                        top: point.y,
                        radius: 6,
                        angle: 0,
                        fill: '#FC0',
                        centeredScaling: true,
                        selectable: true,
                        transparentCorners: true,
                        hasBorders: false,
                        hasControls: false,
                        hasRotatingPoint: false,
                        data: {
                            id: uuid(),
                            type: 'Edge',
                            index: index,
                            polygon_id: polygon.data.id,
                            template_id: polygon.data.template_id,
                        },
                        opacity: 1,
                    })
                )
            })

            setHelpers((prev) => {
                const map = new Map(prev)
                map.set(polygon.data.id, edges)
                return map
            })

            canvas.add(...edges)
            canvas.requestRenderAll()
        },
        [canvas]
    )

    const submitIndividualization = async (
        method: 'store' | 'update' | 'duplicate'
    ) => {
        if (!article) return
        const data = {
            id: individualizationId,
            kArtikel: article.kArtikel,
            variations: filteredVariations
                ? JSON.stringify(filteredVariations)
                : null,
            templates: JSON.stringify(templates),
            spots: JSON.stringify(
                spots.map((spot) => {
                    return {
                        ...spot.data,
                        fabric: JSON.parse(JSON.stringify(spot)),
                    }
                })
            ),
            measurements: JSON.stringify(
                measurements.map((measurement) => {
                    const orientation =
                        measurement.line.data.orientation ?? 'horizontal'
                    const center = measurement.line.getCenterPoint()

                    return {
                        id: measurement.line.data.id,
                        template_id: measurement.line.data.template_id,
                        real_length: parseInt(
                            measurement.line.data.real_length
                        ),
                        orientation: orientation,
                        length:
                            orientation === 'horizontal'
                                ? measurement.line.width
                                : measurement.line.height,
                        center_x: center.x,
                        center_y: center.y,
                        color: measurement.line.fill,
                    }
                })
            ),
            has_measurement: hasMeasurement,
            surcharge_kArtikel: globalSurcharge,
        }

        if (method === 'update') {
            await api
                .post(ApiRoutes.INDIVIDUALIZATIONS, individualizationId, data)
                .then((response) => {
                    if (response.status === 200 || response.status === 201) {
                        showToast(
                            'Individualisierung wurde erfolgreich aktualisiert.'
                        )
                    } else {
                        showToast(
                            'Fehler beim Aktualisieren der Individualisierung.',
                            'error'
                        )
                    }
                })
        } else {
            await api
                .post(ApiRoutes.INDIVIDUALIZATIONS, undefined, data)
                .then((response) => {
                    if (response.status === 200 || response.status === 201) {
                        showToast(
                            'Individualisierung wurde erfolgreich hinzugefügt.'
                        )
                    } else {
                        showToast(
                            'Fehler beim Hinzufügen der Individualisierung.',
                            'error'
                        )
                    }
                })
        }
    }

    const loadIndividualization = useCallback(
        (individualization: StoredIndividualization) => {
            setIndividualizationId(individualization.id)
            setGlobalSurcharge(individualization.surcharge_kArtikel)
            setChosenVariations(individualization.variations ?? {})
            setHasMeasurement(individualization.has_measurement ?? false)

            const individualization_templates: Template[] = []
            const individualization_spots: fabric.Object[] = []
            const individualization_measurements: {
                start: fabric.Rect
                end: fabric.Rect
                line: fabric.Rect
            }[] = []

            individualization.templates.forEach((storedTemplate) => {
                const template: Template = {
                    id: storedTemplate.id,
                    name: storedTemplate.name,
                    name_multi: storedTemplate.name_multi,
                    src: storedTemplate.src,
                    individualization_id: individualization.id,
                    surcharge_kArtikel: storedTemplate.surcharge_kArtikel,
                }

                individualization_templates.push(template)

                if (storedTemplate.measurement) {
                    const storedMeasurement = storedTemplate.measurement

                    const measurement = addMeasurement(
                        storedTemplate.id,
                        storedMeasurement.id,
                        storedMeasurement.color ?? '#FC0',
                        new fabric.Point(
                            storedMeasurement.center_x,
                            storedMeasurement.center_y
                        ),
                        storedMeasurement.orientation,
                        storedMeasurement.length,
                        storedMeasurement.real_length
                    )

                    individualization_measurements.push(measurement)
                }

                storedTemplate.spots.forEach((storedSpot) => {
                    const { type, ...rest } = storedSpot.fabric
                    const style = {
                        opacity: 0.3,
                        fill: '#FC0',
                        borderColor: '#FC0',
                        cornerColor: '#FC0',
                        cornerStyle: 'circle' as 'circle' | 'rect' | undefined,
                        cornerSize: 12,
                        transparentCorners: false,
                        centeredScaling: true,
                        selectable: true,
                    }

                    let element:
                        | fabric.Circle
                        | fabric.Ellipse
                        | fabric.Rect
                        | fabric.Polygon
                        | null = null
                    const edges: fabric.Circle[] = []

                    switch (type) {
                        case 'circle':
                            element = new fabric.Circle({ ...rest, ...style })
                            break
                        case 'rect':
                            element = new fabric.Rect({ ...rest, ...style })
                            break
                        case 'ellipse':
                            element = new fabric.Ellipse({ ...rest, ...style })
                            break
                        case 'polygon':
                            element = new fabric.Polygon([], {
                                ...rest,
                                ...style,
                            })

                            element.setControlsVisibility({
                                tl: true,
                                tr: true,
                                br: true,
                                bl: true,
                                mt: false,
                                mb: false,
                                ml: false,
                                mr: false,
                                mtr: false,
                            })

                            element.points?.forEach((point, index) => {
                                edges.push(
                                    new fabric.Circle({
                                        originX: 'center',
                                        originY: 'center',
                                        left: point.x,
                                        top: point.y,
                                        radius: 8,
                                        angle: 0,
                                        fill: '#FC0',
                                        centeredScaling: true,
                                        selectable: true,
                                        transparentCorners: true,
                                        hasBorders: false,
                                        hasControls: false,
                                        hasRotatingPoint: false,
                                        data: {
                                            id: uuid(),
                                            type: 'Edge',
                                            index: index,
                                            template_id: template.id,
                                            polygon_id: storedSpot.id,
                                        },
                                        opacity: 1,
                                    })
                                )
                            })

                            setHelpers((prev) => {
                                const map = new Map(prev)
                                map.set(storedSpot.id, edges)
                                return map
                            })

                            break
                        default:
                            break
                    }

                    if (element) {
                        element.data = {
                            id: storedSpot.id,
                            template_id: storedSpot.template_id,
                            name: storedSpot.name,
                            name_multi: storedSpot.name_multi ?? '',
                            hint: storedSpot.hint ?? '',
                            hint_multi: storedSpot.hint_multi ?? '',
                            type: storedSpot.type,
                            surcharge_kArtikel: storedSpot.surcharge_kArtikel,
                            text_color: storedSpot.text_color,
                            multiline: storedSpot.multiline,
                            media_category_filtered:
                                storedSpot.media_category_filtered,
                            grayscale: storedSpot.grayscale,
                            individual_text_color:
                                storedSpot.individual_text_color,
                            individual_font_size:
                                storedSpot.individual_font_size,
                            individual_font_family:
                                storedSpot.individual_font_family,
                            font_size: storedSpot.font_size,
                            font_families:
                                storedSpot.font_families?.toString() ?? '',
                            max_character: storedSpot.max_character,
                            sync_id: storedSpot.sync_id,
                            media_upload: storedSpot.media_upload,
                            media_library: storedSpot.media_library,
                            max_text_fields: storedSpot.max_text_fields,
                            max_image_fields: storedSpot.max_image_fields,
                            media_category_ids: storedSpot.media_category_ids,
                            fixed_user_inputs: storedSpot.fixed_user_inputs,
                        }

                        individualization_spots.push(element)
                    }
                })
            })

            setTemplates(individualization_templates)
            setSpots(individualization_spots)
            setMeasurements(individualization_measurements)
        },
        [addMeasurement]
    )

    useEffect(() => {
        fetchFonts()
    }, [fetchFonts])

    useEffect(() => {
        fetchCategories()
    }, [fetchCategories])

    const prevIndividualizationId = useRef<string | number | null>(null)
    useEffect(() => {
        if (
            individualization &&
            individualization.id !== prevIndividualizationId.current
        ) {
            prevIndividualizationId.current = individualization.id

            fetchArticle(individualization.kArtikel)
            loadIndividualization(individualization)
        }
    }, [fetchArticle, individualization, loadIndividualization])

    useEffect(() => {
        const completed = []
        if (article) completed.push(0)
        if (
            templates.length > 0 &&
            templates.filter((t) => t.name.length === 0).length === 0
        )
            completed.push(1)
        if (
            !hasMeasurement ||
            templates.every((template) => {
                return !!measurements.find(
                    (r) => r.line.data.template_id === template.id
                )
            })
        ) {
            completed.push(2)
        }
        if (
            spots.length > 0 &&
            spots.filter((s) => {
                return !s.data?.name || s.data.name.length === 0 || !s.data.type
            }).length === 0
        )
            completed.push(2)
        completed.push(3)
        setCompletedSteps(completed)
    }, [article, templates, spots, measurements, hasMeasurement])

    const errors: string[] = useMemo(() => {
        const errorList: string[] = []

        if (visitedSteps.includes(0) && !article) {
            errorList.push(t('error.noArticle'))
        }
        if (visitedSteps.includes(1)) {
            const noNameTemplateCount = templates.filter(
                (t) => t.name.length === 0
            ).length
            if (templates.length === 0) {
                errorList.push(t('error.noTemplate'))
            } else if (noNameTemplateCount > 0) {
                if (noNameTemplateCount === 1) {
                    errorList.push(
                        t('error.noTemplateNameSingle', {
                            number: noNameTemplateCount,
                        })
                    )
                } else {
                    errorList.push(
                        t('error.noTemplateNameMulti', {
                            number: noNameTemplateCount,
                        })
                    )
                }
            }
        }

        if (visitedSteps.includes(2)) {
            if (
                hasMeasurement &&
                !templates.every((template) => {
                    return !!measurements.find(
                        (r) => r.line.data.template_id === template.id
                    )
                })
            ) {
                errorList.push(t('error.noMeasurementForTemplate'))
            } else if (
                measurements.some((measurement) => {
                    return (
                        measurement.line.data.real_length === undefined ||
                        measurement.line.data.real_length === null ||
                        measurement.line.data.real_length === ''
                    )
                })
            ) {
                errorList.push(t('error.noMeasurementLength'))
            }
        }

        if (visitedSteps.includes(3)) {
            const noNameSpotCount = spots.filter((s) => {
                return !s.data?.name || s.data.name.length === 0
            }).length
            const noTypeSpotCount = spots.filter((s) => {
                return !s.data?.type
            }).length

            if (spots.length === 0) {
                errorList.push(t('error.noSpot'))
            } else {
                if (noNameSpotCount > 0) {
                    if (noNameSpotCount === 1) {
                        errorList.push(
                            t('error.noSpotNameSingle', {
                                number: noNameSpotCount,
                            })
                        )
                    } else {
                        errorList.push(
                            t('error.noSpotNameMulti', {
                                number: noNameSpotCount,
                            })
                        )
                    }
                }
                if (noTypeSpotCount > 0) {
                    if (noTypeSpotCount === 1) {
                        errorList.push(
                            t('error.noSpotTypeSingle', {
                                number: noTypeSpotCount,
                            })
                        )
                    } else {
                        errorList.push(
                            t('error.noSpotTypeMulti', {
                                number: noTypeSpotCount,
                            })
                        )
                    }
                }
            }
        }

        return errorList
    }, [
        visitedSteps,
        article,
        t,
        templates,
        hasMeasurement,
        measurements,
        spots,
    ])

    useEffect(() => {
        // remove all event listeners from the canvas
        canvas.off()

        canvas
            .on('selection:cleared', () => {
                changeActiveSpot(null)
            })
            .on('selection:created', (e) => {
                if (e.selected !== undefined && e.selected.length > 0) {
                    if (e.selected[0].data?.type === 'Edge')
                        changeActiveSpot(e.selected[0].data.polygon_id)
                    else if (e.selected[0].data?.type === 'Example')
                        changeActiveSpot(e.selected[0].data.spot_id)
                    else if (
                        e.selected[0].data?.type !== 'Measurement' &&
                        e.selected[0].data?.id
                    )
                        changeActiveSpot(e.selected[0].data.id)
                }
            })
            .on('selection:updated', (e) => {
                if (e.selected !== undefined && e.selected.length > 0) {
                    if (e.selected[0].data?.type === 'Edge')
                        changeActiveSpot(e.selected[0].data.polygon_id)
                    else if (e.selected[0].data?.type === 'Example')
                        changeActiveSpot(e.selected[0].data.spot_id)
                    else if (
                        e.selected[0].data?.type !== 'Measurement' &&
                        e.selected[0].data?.id
                    )
                        changeActiveSpot(e.selected[0].data.id)
                }
            })
            .on('object:moving', (e) => {
                if (e.target && e.target.data?.type === 'Edge') {
                    syncHelper(e.target as fabric.Circle)
                }
                if (e.target && e.target.data?.type === 'Measurement') {
                    updateMeasurement(e.target as fabric.Rect)
                }
                if (e.target && e.target instanceof fabric.Polygon) {
                    resetHelpers(e.target)
                }
            })
            .on('object:scaling', (e) => {
                if (e.target && e.target instanceof fabric.Polygon) {
                    resetHelpers(e.target)
                }
            })
            .on('object:modified', (e) => {
                if (
                    e.target &&
                    e.target.data?.type !== 'Edge' &&
                    e.target.data?.type !== 'Example' &&
                    e.target.data?.type !== 'Measurement'
                ) {
                    syncSpot(rescaleFabricObject(e.target))
                    removeExample(e.target.data.id)
                }
                if (e.target && e.target instanceof fabric.Polygon) {
                    resetHelpers(e.target)
                    initializeHelpers(
                        rescaleFabricObject(e.target) as fabric.Polygon
                    )
                }
            })
    }, [
        canvas,
        initializeHelpers,
        removeExample,
        resetHelpers,
        syncHelper,
        syncSpot,
    ])

    useEffect(() => {
        setActiveTemplateId((prev) => {
            if (prev) return prev
            if (templates.length > 0) return templates[0].id
            else return null
        })
    }, [templates])

    return (
        <IndividualizationContext.Provider
            value={{
                individualizationId,
                steps,
                fonts,
                categories,
                completedSteps,
                errors,
                activeStep,
                visitedSteps,
                changeActiveStep,
                templates,
                uploadTemplate,
                changeTemplateName,
                changeTemplateSurcharge,
                removeTemplate,
                article,
                setArticle,
                fetchArticle,
                spots,
                addSpot,
                removeSpot,
                syncSpot,
                syncHelper,
                updateSpot,
                updateSpotData,
                addEdgeToPolygon,
                initializeHelpers,
                removeEdgeFromPolygon,
                helpers,
                addHelper,
                examples,
                setExamples,
                globalSurcharge,
                updateGlobalSurcharge,
                submitIndividualization,
                canvas,
                activeSpotId,
                activeSpot,
                changeActiveSpot,
                activeTemplateId,
                setActiveTemplateId,
                activeTemplate,
                chosenVariations,
                setChosenVariations,
                measurements,
                setMeasurements,
                hasMeasurement,
                setHasMeasurement,
                addMeasurement,
            }}
        >
            {children}
        </IndividualizationContext.Provider>
    )
}

export const useIndividualizationContext = () => {
    const data = useContext(IndividualizationContext)

    if (data === undefined) {
        throw new Error(
            'useIndividualizationContext must used within IndividualizationContextProvider'
        )
    }

    return data
}
